import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type StreamingMode = 'webrtc' | 'hls';

interface StreamingModeContextType {
  mode: StreamingMode;
  setMode: (mode: StreamingMode) => void;
}

const StreamingModeContext = createContext<StreamingModeContextType | undefined>(undefined);

const STORAGE_KEY = 'streamingMode';

function getInitialMode(): StreamingMode {
  const fromEnv = (process.env.REACT_APP_STREAMING_MODE || '').toLowerCase();
  if (fromEnv === 'hls' || fromEnv === 'webrtc') return fromEnv;

  const fromStorage = (localStorage.getItem(STORAGE_KEY) || '').toLowerCase();
  if (fromStorage === 'hls' || fromStorage === 'webrtc') return fromStorage;

  // Por defecto: página HTML MediaMTX (campo streams.webrtc, sin WHEP)
  return 'webrtc';
}

export const StreamingModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<StreamingMode>(() => getInitialMode());

  const setMode = (next: StreamingMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    // Mantener storage en sync si cambió el default por env (o storage previo)
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }, [mode]);

  const value = useMemo(() => ({ mode, setMode }), [mode]);

  return <StreamingModeContext.Provider value={value}>{children}</StreamingModeContext.Provider>;
};

export const useStreamingMode = () => {
  const ctx = useContext(StreamingModeContext);
  if (!ctx) {
    throw new Error('useStreamingMode must be used within a StreamingModeProvider');
  }
  return ctx;
};

